import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import ScrollToTop from "@/components/feature/ScrollToTop";
import WorkCard from "@/components/feature/WorkCard";
import Seo from "@/components/feature/Seo";
import { characters } from "@/mocks/characters";
import { works } from "@/mocks/works";

type SortKey = "latest" | "views" | "likes";

export default function Works() {
  const [charFilter, setCharFilter] = useState<string>("all");
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<SortKey>("latest");

  const list = useMemo(() => {
    let result = works.filter((w) => charFilter === "all" || w.characterId === charFilter);
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      result = result.filter(
        (w) =>
          w.title.toLowerCase().includes(kw) ||
          w.summary.toLowerCase().includes(kw) ||
          w.characterName.includes(keyword.trim()) ||
          w.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    if (sort === "views") return [...result].sort((a, b) => b.views - a.views);
    if (sort === "likes") return [...result].sort((a, b) => b.likes - a.likes);
    return [...result].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [charFilter, keyword, sort]);

  return (
    <>
      <ScrollToTop />
      <Seo
        title="火影忍者同人作品大全 - 按角色筛选人气同人"
        description="火影同人志作品大全，支持按角色与标签筛选、关键词搜索，可查看人气与最新上线的火影忍者同人作品，涵盖日向雏田、春野樱、纲手、宇智波佐助、漩涡鸣人等角色。"
        keywords="火影忍者同人作品,火影同人合集,雏田同人,樱同人,纲手同人,佐助同人,鸣人同人"
      />

      <Navbar />

      <section className="bg-secondary-700">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-secondary-200">
            <Link to="/" className="hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span>同人作品</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-4xl">
            同人作品大全
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-secondary-100 md:text-base">
            浏览全部火影忍者同人作品，按角色、关键词和排序方式自由筛选，找到最合你心意的二次创作。
          </p>
        </div>
      </section>

      {/* Toolbar */}
      <section className="mx-auto w-full max-w-7xl px-4 pt-8 md:px-6">
        <div className="flex flex-col gap-4 rounded-lg bg-background-100 p-4 md:p-5">
          {/* Search + sort */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索作品标题、标签或角色…"
                className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-1 self-stretch sm:self-auto">
              <span className="sr-only">排序方式</span>
              {(
                [
                  { key: "latest", label: "最新更新" },
                  { key: "views", label: "人气最高" },
                  { key: "likes", label: "点赞最多" },
                ] as { key: SortKey; label: string }[]
              ).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSort(opt.key)}
                  className={`flex-1 whitespace-nowrap rounded-md px-4 py-2 text-xs font-medium transition-colors sm:flex-none md:text-sm ${
                    sort === opt.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200 text-foreground-700 hover:bg-background-300"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Character chips */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCharFilter("all")}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors md:text-sm ${
                charFilter === "all"
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-200 text-foreground-700 hover:bg-background-300"
              }`}
            >
              全部角色
            </button>
            {characters.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCharFilter(c.id)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors md:text-sm ${
                  charFilter === c.id
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-200 text-foreground-700 hover:bg-background-300"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-4 text-sm text-foreground-600">
          共找到 <span className="font-bold text-foreground-950">{list.length}</span> 部同人作品
          {charFilter !== "all" && (
            <>
              （角色：
              <span className="font-medium text-primary-700">
                {characters.find((c) => c.id === charFilter)?.name}
              </span>
              ）
            </>
          )}
        </p>
      </section>

      {/* Works grid */}
      <section className="mx-auto w-full max-w-7xl px-4 py-8 pb-20 md:px-6">
        {list.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {list.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg bg-background-100 px-6 py-20 text-center">
            <i className="ri-search-eye-line text-4xl text-secondary-300" aria-hidden="true"></i>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-800">没有找到相关作品</p>
            <p className="mt-2 text-sm text-foreground-600">试试更换关键词或角色筛选条件。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setCharFilter("all");
              }}
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
            >
              清除筛选条件
            </button>
          </div>
        )}
      </section>

      <Footer />
      <BackToTop />
    </>
  );
}